import { Link, useParams } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, ArrowLeft, CheckCircle, Images, Bell, Storefront } from "@phosphor-icons/react";
import LangSwitcher from "./components/LangSwitcher";
import ThemeSwitch from "./components/ThemeSwitch";
import BackButton from "./components/BackButton";
import { Button } from "../components/ui/button";
import GalleryCarousel from "./components/GalleryCarousel";
import ReviewsSection from "./components/ReviewsSection";
import { t, getLocale } from "../lib/i18n";
import { fmtEur } from "../lib/api";
import { useOrgSeo } from "../lib/seo";
import {
  BookingProvider, useBooking,
  STEP_SERVICE, STEP_ADDONS, STEP_SLOT, STEP_DATA, STEP_DONE,
} from "../components/booking/BookingContext";
import CenterInfoCard from "../components/booking/CentroSelector";
import CentroProfileHero from "../components/booking/CentroProfileHero";
import ServicioCard from "../components/booking/ServicioCard";
import AddonsSelector from "../components/booking/AddonsSelector";
import SlotPicker from "../components/booking/SlotPicker";
import CustomerDataForm from "../components/booking/CustomerDataForm";
import { PublicChatLauncher } from "../components/PublicChatLauncher";

/**
 * Sprint H · Batch 2 — Public Booking refactor.
 *
 * Esta página actúa como ORQUESTADOR. Todo el estado vive en `BookingContext`.
 * Cada step pinta su sub-componente correspondiente. Tamaño dramáticamente
 * reducido (~250 LOC) frente a los 647 originales.
 */
function PublicBookingInner() {
  const {
    slug, locale, setLocale, data, loadingOrg, step, setStep,
    booked, resetAll, setService, setDate,
  } = useBooking();
  const tr = (k, params) => t(k, locale, params);

  // Sprint S-1 · SEO: inyecta title + og + JSON-LD desde /api/seo/meta
  useOrgSeo(slug);

  // Default locale fallback (si <App/> aún no ha hidratado nada)
  useEffect(() => { if (!locale) setLocale(getLocale()); }, [locale, setLocale]);

  if (loadingOrg) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Cargando…</div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Centro no encontrado</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-2">404</h1>
        </div>
      </div>
    );
  }

  const org = data.org;
  const hasHero = !!org.hero_image_url;
  const brandColor = (org.brand_color || "#0033A0").trim();
  const heroFallbackBg = `linear-gradient(135deg, #1A1A1A 0%, #1A1A1A 55%, ${brandColor}24 100%)`;
  const brandStyle = { "--centro-brand": brandColor };

  const stepperSteps = [
    { n: STEP_SERVICE, label: tr("paso.servicio") },
    { n: STEP_ADDONS, label: tr("paso.complementos") },
    { n: STEP_SLOT, label: tr("paso.fecha") },
    { n: STEP_DATA, label: tr("paso.datos") },
  ];

  const goBackFromStep = () => {
    if (step === STEP_DATA) setStep(STEP_SLOT);
    else if (step === STEP_SLOT) setStep(STEP_ADDONS);
    else if (step === STEP_ADDONS) setStep(STEP_SERVICE);
    else if (step === STEP_SERVICE) window.history.back();
  };

  return (
    <div className="min-h-screen ag-light-surface" style={brandStyle} data-testid={`public-booking-${slug}`}>
      {/* Breadcrumb · Volver al Marketplace · ficha del centro — sprint Visual V2 */}
      <div className="bg-[#1A1A1A] border-b border-white/10" data-testid="public-booking-back-bar">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-10 flex items-center justify-between gap-3 text-[11px]">
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-1.5 text-white/60 hover:text-[#FFB703] transition-colors"
            data-testid="public-booking-back-marketplace"
          >
            <ArrowLeft size={13} weight="bold" /> Volver al Marketplace
          </Link>
          <Link
            to={`/centro/${slug}`}
            className="inline-flex items-center gap-1.5 text-white/60 hover:text-[#FFB703] transition-colors"
            data-testid="public-booking-back-centro"
          >
            <Storefront size={13} weight="duotone" /> Ver ficha del centro
          </Link>
        </div>
      </div>

      {/* Top bar */}
      <div className="ag-hero bg-[#1A1A1A] border-b relative" style={{ borderBottomColor: brandColor + "40" }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 relative">
          <div className="absolute left-0 right-0 top-0 h-[3px]" style={{ backgroundColor: brandColor }} />
          <div className="flex items-center gap-2.5 min-w-0 relative">
            {org.logo_url ? (
              <img src={org.logo_url} alt={org.name} className="w-9 h-9 rounded bg-white object-contain p-0.5 ring-1" style={{ ringColor: brandColor }} />
            ) : (
              <div className="w-9 h-9 rounded text-[var(--ag-text)] grid place-items-center font-display font-black"
                   style={{ backgroundColor: brandColor }}>
                {org.name?.charAt(0).toUpperCase() || "?"}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-display font-bold text-sm sm:text-base leading-none truncate text-[var(--ag-text)]">{org.name}</div>
              <div className="text-[9px] uppercase tracking-[0.2em] mt-1" style={{ color: brandColor }}>{tr("reserva.online")}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 relative">
            <ThemeSwitch />
            <LangSwitcher locale={locale} onChange={setLocale} />
          </div>
        </div>
      </div>

      {/* Hero premium del centro (sólo step inicial) — incluye cover, info card, tabs (trabajos/reviews/horario/promos) */}
      {step === STEP_SERVICE && (
        <CentroProfileHero
          org={org}
          slug={slug}
          brandColor={brandColor}
          onStartBooking={() => {
            const el = document.querySelector('[data-testid="services-grid"]');
            el?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        />
      )}

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-32 sm:pb-12">
        {/* Banner vacaciones */}
        {org.vacation_mode_active && (
          <div className="rounded-md border-2 border-amber-400 bg-amber-50 p-4 mb-6 flex items-start gap-3" data-testid="vacation-banner">
            <Bell size={20} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <div className="font-bold uppercase tracking-wider text-xs mb-0.5">Centro cerrado por vacaciones</div>
              <div>{org.vacation_message || "Estamos de vacaciones temporalmente."}</div>
              {org.vacation_until && <div className="text-xs mt-1 opacity-80">Reabrimos: {org.vacation_until}</div>}
            </div>
          </div>
        )}

        {/* Stepper — dots premium */}
        {step < STEP_DONE && (
          <div className="flex items-center justify-between gap-3 mb-5 sm:mb-6 flex-wrap">
            <BackButton onClick={goBackFromStep} label={tr("atras")} variant="light" testId="pub-back-top" />
            <div className="flex items-center gap-2" data-testid="step-dots">
              {stepperSteps.map((s, i) => {
                const isActive = step === s.n;
                const isDone = step > s.n;
                return (
                  <div key={s.n} className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`relative grid place-items-center transition-all ${
                          isActive ? "w-8 h-8" : "w-2.5 h-2.5"
                        }`}
                      >
                        <span
                          className={`rounded-full transition-all ${
                            isActive
                              ? "w-8 h-2.5 bg-[#D4AF37] dark:bg-[#F5C76A] shadow-md shadow-amber-900/30"
                              : isDone
                              ? "w-2.5 h-2.5 bg-[#D4AF37] dark:bg-[#F5C76A]"
                              : "w-2.5 h-2.5 bg-gray-300 dark:bg-white/15"
                          }`}
                        />
                      </span>
                      <span className={`text-[10px] uppercase tracking-[0.15em] font-bold transition-colors hidden sm:inline ${
                        isActive ? "text-[#1A1A1A] dark:text-white" : isDone ? "text-[#B8860B] dark:text-[#F5C76A]" : "text-[var(--ag-text-muted)] dark:text-gray-500"
                      }`}>
                        {i + 1}·{s.label}
                      </span>
                    </div>
                    {i < stepperSteps.length - 1 && (
                      <span className={`h-px w-3 sm:w-6 transition-colors ${
                        isDone ? "bg-[#D4AF37] dark:bg-[#F5C76A]" : "bg-gray-200 dark:bg-white/10"
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Steps */}
        {step === STEP_SERVICE && <ServicioCard />}
        {step === STEP_ADDONS && <AddonsSelector />}
        {step === STEP_SLOT && <SlotPicker />}
        {step === STEP_DATA && <CustomerDataForm />}

        {/* DONE */}
        {step === STEP_DONE && booked && (
          <div className="bg-white border border-gray-200 rounded-sm p-10 text-center max-w-xl mx-auto" data-testid="pub-confirmed">
            {booked.status === "pending_review" ? (
              <>
                <div className="w-14 h-14 mx-auto bg-amber-100 grid place-items-center rounded-full">
                  <CheckCircle size={32} weight="duotone" className="text-amber-600" />
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-amber-700 mt-4" data-testid="pub-pending-review-tag">Pendiente de validación</p>
                <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1 text-[#1A1A1A]">Hemos recibido tu solicitud</h2>
                <p className="text-sm text-gray-600 mt-3 max-w-md mx-auto">
                  Este servicio requiere una revisión previa por parte del taller. El equipo te confirmará la cita
                  o se pondrá en contacto contigo en breve.
                </p>
              </>
            ) : (
              <>
                <div className="w-14 h-14 mx-auto bg-emerald-100 grid place-items-center rounded-full">
                  <CheckCircle size={32} weight="duotone" className="text-emerald-600" />
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mt-4">{tr("reserva.confirmada")}</p>
                <h2 className="font-display text-4xl font-bold tracking-tighter mt-1 text-[#1A1A1A]">{tr("te.esperamos")}</h2>
              </>
            )}
            <div className="bg-gray-50 rounded-sm p-4 mt-6 text-sm tabular-nums space-y-1 text-left">
              <div className="flex justify-between"><span className="text-gray-500">{tr("servicio")}</span><b>{booked.wash_type}</b></div>
              <div className="flex justify-between">
                <span className="text-gray-500">{tr("fecha")}</span>
                <b>{new Date(booked.date).toLocaleString(locale === "en" ? "en-GB" : (locale === "pt" ? "pt-PT" : (locale === "ca" ? "ca-ES" : "es-ES")))}</b>
              </div>
              <div className="flex justify-between"><span className="text-gray-500">{tr("matricula")}</span><b className="font-mono-tab">{booked.license_plate}</b></div>
              {(booked.addons || []).length > 0 && (
                <>
                  <div className="border-t my-2 border-gray-200" />
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">{tr("paso.complementos")}</div>
                  {booked.addons.map((a) => (
                    <div key={a.id} className="flex justify-between text-xs"><span>{a.name} ×{a.qty}</span><b>{fmtEur(a.subtotal)}</b></div>
                  ))}
                </>
              )}
              <div className="border-t my-2 border-gray-200" />
              <div className="flex justify-between text-base">
                <span className="text-gray-500">{tr("total")}</span>
                <b className="text-[var(--ag-gold)]">{fmtEur(booked.total_price)}</b>
              </div>
            </div>
            <Button onClick={resetAll} variant="outline" className="mt-6 rounded-sm" data-testid="pub-book-another">
              {tr("reservar.otra")}
            </Button>
          </div>
        )}
      </div>

      {/* Sprint IA-2 · #18 · Widget de chat público con IA */}
      {/* Sprint IA-3 · #20 · Handoff IA → reserva (selecciona servicio + scroll al stepper) */}
      {/* Sprint UNIFY · #21 · Lanzador único con picker IA / Humano */}
      <PublicChatLauncher
        orgSlug={slug}
        orgName={org.name}
        onSelectService={(svc) => {
          // Buscar el servicio completo en el catálogo (con add-ons, duración, etc.)
          const full = (data?.catalog || []).find((c) => c.id === svc.id) || svc;
          setService(full);
          // IA-4 · si la IA propuso fecha, la aplicamos y avanzamos al picker de slots
          if (svc.suggested_date) {
            try {
              const d = new Date(svc.suggested_slot_iso || `${svc.suggested_date}T10:00`);
              if (!isNaN(d.getTime()) && d > new Date()) {
                setDate(d);
                // Si el servicio no tiene addons, saltamos directamente a SLOT
                const hasAddons = (full.addons || []).length > 0;
                setStep(hasAddons ? STEP_ADDONS : STEP_SLOT);
              } else {
                setStep(STEP_ADDONS);
              }
            } catch {
              setStep(STEP_ADDONS);
            }
          } else {
            setStep(STEP_ADDONS);
          }
          // Scroll suave a la zona del stepper
          setTimeout(() => {
            const el = document.querySelector('[data-testid^="public-booking-"]');
            if (el) window.scrollTo({ top: el.offsetTop + 100, behavior: "smooth" });
          }, 150);
        }}
      />

      {/* Sticky footer CTA — solo en step inicial (STEP_SERVICE) para empujar a elegir servicio */}
      {step === STEP_SERVICE && (
        <div
          className="fixed bottom-0 inset-x-0 z-40 px-4 py-3 sm:py-4
            bg-white/95 dark:bg-[#0F1117]/95 backdrop-blur-md border-t border-gray-200 dark:border-white/10
            shadow-[0_-10px_30px_-15px_rgba(0,0,0,0.25)]"
          data-testid="sticky-booking-cta"
        >
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500 dark:text-gray-400">
                Paso 1 · Servicio
              </div>
              <div className="text-sm font-semibold text-[#1A1A1A] dark:text-white truncate">
                Selecciona un servicio para continuar
              </div>
            </div>
            <button
              onClick={() => {
                const el = document.querySelector('[data-testid="services-grid"]');
                el?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              data-testid="sticky-cta-button"
              className="h-11 px-5 rounded-xl font-bold text-sm flex items-center gap-2 transition active:scale-[0.98] shadow-lg shadow-amber-900/20
                bg-gradient-to-r from-[#D4AF37] to-[#B8860B] hover:from-[#B8860B] hover:to-[#996F0A]
                dark:from-[#F5C76A] dark:to-[#D4AF37] dark:hover:from-[#D4AF37] dark:hover:to-[#B8860B]
                text-[#1A1A1A]"
            >
              Reservar <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PublicBooking() {
  const { slug } = useParams();
  return (
    <BookingProvider slug={slug}>
      <PublicBookingInner />
    </BookingProvider>
  );
}
